<template>
    <div id="report-pdf-wrapper" class="bg-white p-6 rounded-xl shadow-sm border border-gray-100 relative">
        
        <!-- TOP CONTROLS: CRITERIA & SEARCH -->
        <div class="flex justify-between items-end w-full mb-5 gap-4 flex-wrap">
            
            <div class="flex items-end gap-4">
    <!-- Left: Criteria Dropdown -->
    <div class="flex flex-col items-start">
        <label class="text-sm font-medium mb-1 text-gray-600">{{ $t('orgDashboard.reports.criteria') }}</label>
        
        <!-- Added explicit inline styles for border, radius, and height to override native browser defaults -->
        <select v-model="yAxis"
            style="border: 1px solid #9ca3af; border-radius: 0.5rem; height: 40px; box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);"
            class="px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 w-56 text-sm text-gray-700 cursor-pointer">
            <option v-for="param in parameters" :key="`y-${param}`" :value="param">
                {{ param }}
            </option>
        </select>
    </div>

    <!-- Right: Custom Role Filter Dropdown -->
    <div class="flex flex-col items-start relative" ref="roleDropdown">
        <label class="text-sm font-medium mb-1 text-gray-600">Filter by Role</label>
        
        <!-- Matched the exact same inline styles here -->
        <div tabindex="0" @click="isRoleDropdownOpen = !isRoleDropdownOpen"
             style="border: 1px solid #9ca3af; border-radius: 0.5rem; height: 40px; box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);"
             class="px-3 py-2 bg-white w-48 cursor-pointer flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-purple-200 text-sm text-gray-700 transition-all">
            
            <span class="truncate select-none">
                {{ selectedRoleFilter.includes('All Roles') ? 'All Roles' : selectedRoleFilter.length + ' Selected' }}
            </span>
            
            <!-- Darker, thicker SVG to mimic the native select arrow -->
            <svg class="w-4 h-4 text-gray-800 flex-shrink-0 ml-2 transition-transform duration-200" 
                 :class="{'rotate-180': isRoleDropdownOpen}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"></path>
            </svg>
        </div>

        <!-- Dropdown Menu Content -->
        <div v-if="isRoleDropdownOpen" 
             class="absolute top-[65px] left-0 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-60 overflow-y-auto custom-scrollbar">
            
            <div class="p-1">
                <!-- All Roles Checkbox -->
                <label class="flex items-center px-3 py-2 hover:bg-purple-50 rounded cursor-pointer transition-colors m-0">
                    <input type="checkbox" value="All Roles" v-model="selectedRoleFilter" 
                           class="mr-3 w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500 cursor-pointer">
                    <span class="text-sm text-gray-800 font-medium">All Roles</span>
                </label>
                
                <div class="h-px bg-gray-100 my-1 mx-2"></div>
                
                <!-- Dynamic Roles Checkboxes -->
                <label v-for="role in availableRoles" :key="`role-${role}`" 
                       class="flex items-center px-3 py-2 hover:bg-purple-50 rounded cursor-pointer transition-colors m-0">
                    <input type="checkbox" :value="role" v-model="selectedRoleFilter" 
                           class="mr-3 w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500 cursor-pointer">
                    <span class="text-sm text-gray-700 truncate" :title="role">{{ role }}</span>
                </label>
            </div>
        </div>
    </div>
</div>

            <!-- Right: Search Bar AND Download Button -->
            <div class="flex items-end gap-4 ml-auto">
                <div class="flex flex-col items-start" style="width: 250px;">
                    <label class="text-sm font-medium mb-1 text-gray-600">Search</label>
                    <div class="relative w-full">
                        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                            </svg>
                        </div>
                        <input v-model="searchQuery" type="text" placeholder="Search name or role..."
                            class="w-full border border-gray-200 rounded-full pl-10 pr-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 text-sm shadow-sm transition-all">
                    </div>
                </div>

                <!-- Clean Download PDF Button -->
                <div class="relative group inline-flex items-center justify-center">
                    <button
                        @click="downloadPDF"
                        :disabled="isDownloading"
                        style="background: transparent; border: none; box-shadow: none; outline: none;"
                        class="p-1 text-yellow-500 hover:text-yellow-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-wait cursor-pointer">
                        
                        <!-- Normal Download Icon -->
                        <svg v-if="!isDownloading" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                        </svg>

                        <!-- Spinning Loader Icon (Shows when downloading) -->
                        <svg v-else class="w-6 h-6 animate-spin text-yellow-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    </button>

                    <!-- Tooltip (Changes text based on state) -->
                    <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 
                                opacity-0 group-hover:opacity-100
                                px-2 py-1 text-[11px] text-gray-200 
                                bg-[#1e1e1e] border border-[#2d2d2d] 
                                rounded-md shadow-lg whitespace-nowrap 
                                pointer-events-none z-50 transition-opacity duration-150">
                        {{ isDownloading ? 'Generating PDF...' : 'Download Report' }}
                    </div>
                </div>
            </div>
        </div>

        <div id="table-scroll-container" class="overflow-x-auto w-full mt-2">
            
            <table class="min-w-full border border-gray-200" style="table-layout: fixed">
                <thead>
                    <tr>
                        <th @click="handleSort('name')" class="name px-4 py-3 text-left font-semibold text-sm text-gray-600 border border-gray-200 bg-gray-50 w-[500px] cursor-pointer hover:bg-gray-100 select-none">
                            <div class="flex items-center">
                                {{ $t('orgDashboard.reports.talent') }}
                                <span v-if="sortKey === 'name'" class="ml-1 text-[10px]">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                            </div>
                        </th>
                        <th @click="handleSort('role')" class="px-4 py-3 text-left font-semibold text-sm text-gray-600 border border-gray-200 bg-gray-50 w-[140px] cursor-pointer hover:bg-gray-100 select-none">
                            <div class="flex items-center">
                                {{ $t('orgDashboard.reports.role') }}
                                <span v-if="sortKey === 'role'" class="ml-1 text-[10px]">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                            </div>
                        </th>
                        <th v-for="(header, index) in assessmentData.scoreHeaders" :key="index" @click="handleSort('criteria', index)" class="px-4 py-3 text-center font-semibold text-sm text-gray-600 border border-gray-200 bg-gray-50 whitespace-nowrap w-[140px] cursor-pointer hover:bg-gray-100 select-none">
                            <div class="flex items-center justify-center">
                                {{ header.toUpperCase() }}
                                <span v-if="sortKey === 'criteria' && sortIndex === index" class="ml-1 text-[10px]">
                                    {{ sortOrder === 'asc' ? '▲' : '▼' }}
                                </span>
                            </div>
                        </th>
                        <th @click="handleSort('total')" class="px-4 py-3 text-center font-semibold text-sm text-gray-600 border border-gray-200 bg-gray-50 w-[140px] cursor-pointer hover:bg-gray-100 select-none">
                            <div class="flex items-center justify-center">
                                {{ $t('Total') }}
                                <span v-if="sortKey === 'total'" class="ml-1 text-[10px]">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                            </div>
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <!-- WEIGHTAGE ROW -->
                    <tr>
                        <td class="px-4 py-4 whitespace-nowrap border border-gray-200">
                            <div class="font-medium text-sm text-gray-700">{{ $t('orgDashboard.reports.weightage') }}</div>
                        </td>
                        <td class="px-4 py-4 whitespace-nowrap border border-gray-200"></td>
                        <td v-for="(header, index) in assessmentData.scoreHeaders" :key="index"
                            class="px-4 py-4 text-center border border-gray-200">
                            <div class="text-sm text-gray-600">
                                {{ assessmentData.weightages[index] }}
                            </div>
                        </td>
                        <td class="px-4 py-4 text-center whitespace-nowrap border border-gray-200">
                            <div class="text-sm text-gray-600">100</div>
                        </td>
                    </tr>

                    <!-- DYNAMIC EMPLOYEE ROWS -->
                    <tr v-for="(employee, empIndex) in processedEmployees" :key="employee.userId + '-' + empIndex">
                        
                        <td class="px-4 py-4 border border-gray-200 cursor-pointer hover:bg-purple-50 transition-colors group"
                            @click="openTalentCard(employee.userId)">
                            <div class="flex items-start">
                                <div class="flex-shrink-0 h-10 w-10">
                                    <img class="h-10 w-10 rounded-full" :src="employee.avatar" alt="" />
                                </div>
                                <div class="ml-4">
                                    <div class="font-medium text-sm text-gray-900 group-hover:text-purple-700 transition-colors pt-1">
                                        {{ employee.name }}
                                    </div>
                                </div>
                            </div>
                        </td>

                        <!-- Role Cell with Nested Badge -->
                        <td v-if="!employee.hideRoleCell" 
    :rowspan="employee.rowspan"
    class="p-0 border border-gray-200 cursor-pointer hover:bg-purple-50 transition-colors group bg-white align-middle" 
    style="position: relative; height: 1px;" 
    @click="openRoleCard(employee.roleId)">
    
    <!-- Inner container with minimal right padding (pr-2) -->
    <div style="position: relative; height: 100%; width: 100%; min-height: 5rem;" class="flex items-center px-4 py-2">
        
        <!-- Badge: Pinned to Top Right -->
        <div v-if="employee.classification !== 'NVR'" 
             style="position: absolute; top: 4px; right: 4px; z-index: 20;">
            <span class="role-badge" 
                  :class="employee.classification.toLowerCase()" 
                  style="display: block; white-space: nowrap; font-size: 9px; padding: 1px 4px;">
                {{ employee.classification }}
            </span>
        </div>

        <!-- Role Name: pr-2 allows it to use the full width of the cell -->
        <div class="text-xs text-left text-gray-800 font-semibold whitespace-normal w-full pr-2" 
            :title="employee.role">
            {{ employee.role }}
        </div>
        
    </div>
</td>

                        <td v-for="(scoreObj, scoreIndex) in employee.scores" :key="scoreIndex"
                            class="px-4 py-4 text-center border border-gray-200">
                            <div class="inline-flex items-center justify-center w-5 h-5 rounded-full mx-auto cursor-help shadow-sm border border-black/10"
                                :style="{ backgroundColor: getScoreColor(scoreObj.value) }"
                                :title="scoreObj.remarks">
                                <span v-if="scoreObj.value === null || scoreObj.value === undefined"
                                    class="text-xs text-white font-bold">N/A</span>
                            </div>
                        </td>

                        <td class="px-4 py-4 text-center whitespace-nowrap border border-gray-200">
                            <div class="text-sm font-bold" :class="getScoreTextColorClass(employee.totalScore)">
                                {{ employee.totalScore }}
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            
            <div v-if="processedEmployees.length === 0" class="py-8 text-center text-gray-500 w-full border-x border-b border-gray-200 bg-gray-50 rounded-b-lg">
                No matching talent or roles found.
            </div>
        </div>

        <div class="mt-6 flex flex-wrap items-center justify-start gap-x-6 gap-y-3">
            <template v-if="kvdrConfig && kvdrConfig.calculationTypes && kvdrConfig.calculationTypes.length">
                <div v-for="(calc, idx) in kvdrConfig.calculationTypes" :key="idx" class="flex items-center">
                    <div class="w-4 h-4 rounded-full mr-2 shadow-sm" :style="{ backgroundColor: calc.color }"></div>
                    <span class="text-sm text-gray-600">{{ calc.title }} ({{ calc.value }})</span>
                </div>
            </template>
            <template v-else>
                <div class="flex items-center">
                    <div class="w-4 h-4 rounded-full bg-green-500 mr-2"></div>
                    <span class="text-sm text-gray-600">{{ $t('orgDashboard.reports.good') }}</span>
                </div>
                <div class="flex items-center">
                    <div class="w-4 h-4 rounded-full bg-primary mr-2"></div>
                    <span class="text-sm text-gray-600">{{ $t('orgDashboard.reports.average') }}</span>
                </div>
                <div class="flex items-center">
                    <div class="w-4 h-4 rounded-full bg-red-500 mr-2"></div>
                    <span class="text-sm text-gray-600">{{ $t('orgDashboard.reports.poor') }}</span>
                </div>
            </template>

            <div class="flex items-center">
                <div class="w-4 h-4 rounded-full bg-slate-300 mr-2 border border-gray-400"></div>
                <span class="text-sm text-gray-600">{{ $t('orgDashboard.reports.notApplicable') }}</span>
            </div>
        </div>
        <!-- START OF DETAILED REMARKS TABLE (Only shows during PDF Export) -->
        <div v-if="isGeneratingPDF" class="mt-12 w-full pb-8">
            <h2 class="text-xl font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                Detailed Scoring & Remarks
            </h2>
            
            <!-- Added style="table-layout: fixed;" and specific width percentages -->
            <table class="border-collapse border border-gray-200 text-sm w-full" style="table-layout: fixed;">
                <thead class="bg-gray-50">
                    <tr>
                        <th class="px-4 py-3 text-left font-semibold text-gray-600 border border-gray-200" style="width: 20%;">Talent</th>
                        <th class="px-4 py-3 text-left font-semibold text-gray-600 border border-gray-200" style="width: 30%;">Criteria</th>
                        <th class="px-4 py-3 text-center font-semibold text-gray-600 border border-gray-200" style="width: 10%;">Score</th>
                        <th class="px-4 py-3 text-left font-semibold text-gray-600 border border-gray-200" style="width: 40%;">Remarks</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(employee, empIndex) in processedEmployees">
                        <tr v-for="(scoreObj, scoreIndex) in employee.scores" :key="'det-score-' + empIndex + '-' + scoreIndex">
                            
                            <!-- User Info (Strictly Left Aligned, Picture removed for clean format) -->
                            <td v-if="scoreIndex === 0" :rowspan="employee.scores.length" class="px-4 py-4 border border-gray-200 align-top bg-white text-left">
                                <div class="font-bold text-gray-900">{{ employee.name }}</div>
                                <div class="text-xs text-gray-500 mt-1">{{ employee.role }}</div>
                            </td>
                            
                            <!-- Criteria Name & Description (Strictly Left Aligned) -->
                            <td class="px-4 py-4 border border-gray-200 align-top bg-white text-left">
                                <div class="font-semibold text-gray-800">
                                    {{ assessmentData.scoreHeaders[scoreIndex] }}
                                </div>
                                <div v-if="assessmentData.scoreDescriptions[scoreIndex]" 
                                     class="text-xs text-gray-500 mt-1 italic whitespace-normal break-words">
                                    {{ assessmentData.scoreDescriptions[scoreIndex] }}
                                </div>
                            </td>
                            
                            <!-- Score Dot (Strictly Center Aligned) -->
                            <td class="px-4 py-4 border border-gray-200 align-top bg-white text-center">
                                <div class="inline-flex items-center justify-center w-5 h-5 rounded-full shadow-sm border border-black/10 mx-auto"
                                    :style="{ backgroundColor: getScoreColor(scoreObj.value) }">
                                    <span v-if="scoreObj.value === null || scoreObj.value === undefined" class="text-[10px] text-white font-bold">N/A</span>
                                </div>
                            </td>
                            
                            <!-- Text Remarks (Strictly Left Aligned) -->
                            <td class="px-4 py-4 border border-gray-200 align-top bg-white text-left text-gray-600 whitespace-normal break-words">
                                {{ scoreObj.remarks && scoreObj.remarks !== 'No Remarks available.' ? scoreObj.remarks : '-' }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <!-- END OF DETAILED REMARKS TABLE -->

        <RoleCardModal :show="isRoleModalOpen" :roleId="selectedRoleId" @close="isRoleModalOpen = false" />

        <div class="fixed opacity-0 pointer-events-none -z-50 w-0 h-0 overflow-hidden">
            <TalentCardPreview v-if="isTalentModalOpen && selectedUserData" ref="talentCardRef" :userId="selectedUserId" :userData="selectedUserData"/>
        </div>
    </div>
</template>

<script>
import constants from "@/constants";
import { kvdrOrgConfig } from "@/assets/data/kvdrOrgConfigs.js";
import RoleCardModal from "./RoleDetailsCard.vue"; 
import TalentCardPreview from "@/views/org-dashboard/talent-pool/components/TalentCardPreview.vue";
import html2pdf from "html2pdf.js";

export default {
    name: "TalentScorecard",
    components: {
        RoleCardModal,
        TalentCardPreview
    },
    data() {
        return {
            isRoleDropdownOpen: false, // <-- Add this
            selectedRoleFilter: ["All Roles"],
            isGeneratingPDF: false,
            isDownloading: false, 
            searchQuery: "", 
            sortKey: 'name', 
            sortOrder: 'asc',
            sortIndex: null,
            yAxis: "Fitment (Fit)",
            key: "fitmentData",
            kvdrConfig: {},
            
            isRoleModalOpen: false,
            selectedRoleId: "",

            isTalentModalOpen: false,
            selectedUserId: "",
            selectedUserData: null,

            parameters: [
                "Fitment (Fit)",
                "Performance (Drive)",
                "Succession (Secure)",
                "Appraisal (Self)",
            ],
            assessmentData: {
                scoreHeaders: [],
                weightages: [],
                employees: [],
            },
        };
    },
    computed: {
        userId() { return this.$store.state.auth.user._id; },
        orgData() { return this.$store.state.organization.orgData; },
        kvdrData() { return this.$store.state.kvdr.selectedKVDR; },
        employeesData() { return this.$store.state.organization.employees; },
        storeEvaluationMetrics() { return this.$store.state.organization.orgData?.settings?.evaluation_metrics; },
        availableRoles() {
            if (!this.assessmentData.employees) return [];
            const roles = this.assessmentData.employees.map(emp => emp.role);
            // Return unique roles sorted alphabetically
            return [...new Set(roles)].sort((a, b) => a.localeCompare(b));
        },

        // UPDATED: Filters by both Role Dropdown and Search Bar
        filteredEmployees() {
            let employees = this.assessmentData.employees || [];
            
            // 1. Filter by specific Role
            if (this.selectedRoleFilter.length > 0 && !this.selectedRoleFilter.includes("All Roles")) {
                employees = employees.filter(emp => this.selectedRoleFilter.includes(emp.role));
            }

            // 2. Filter by text Search
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                employees = employees.filter(emp =>
                    (emp.name && emp.name.toLowerCase().includes(query)) ||
                    (emp.role && emp.role.toLowerCase().includes(query))
                );
            }
            return employees;
        },
        processedEmployees() {
            const list = this.filteredEmployees;
            const result = [];
            let currentRole = null;
            let currentRoleStartIndex = -1;

            for (let i = 0; i < list.length; i++) {
                const emp = { ...list[i], rowspan: 1, hideRoleCell: false };

                if (emp.role === currentRole) {
                    emp.hideRoleCell = true;
                    if (currentRoleStartIndex !== -1) {
                        result[currentRoleStartIndex].rowspan += 1;
                    }
                } else {
                    currentRole = emp.role;
                    currentRoleStartIndex = i;
                }
                result.push(emp);
            }
            return result;
        }
    },
    watch: {
        selectedRoleFilter(newVal, oldVal) {
        // If "All Roles" was just selected, clear others
        if (newVal.includes("All Roles") && !oldVal.includes("All Roles")) {
            this.selectedRoleFilter = ["All Roles"];
        } 
        // If a specific role was selected while "All Roles" was active, remove "All Roles"
        else if (newVal.length > 1 && newVal.includes("All Roles")) {
            this.selectedRoleFilter = newVal.filter(role => role !== "All Roles");
        }
        // If unselected completely, default back to "All Roles"
        else if (newVal.length === 0) {
            this.selectedRoleFilter = ["All Roles"];
        }
    },
        async kvdrData(val) { if (val) { this.createTalentScorecardData(); } },
        yAxis() { this.createTalentScorecardData(); },
        storeEvaluationMetrics: {
            deep: true,
            handler(newVal) { if (newVal) { this.createTalentScorecardData(); } }
        },
        $route() { if (this.orgData && this.kvdrData) { this.createTalentScorecardData(); } }
    },
    methods: {
        extractTalentCardDescriptions(data) {
            if (!data) return [];
            const result = [];
            for (const category in data) {
                if (Array.isArray(data[category])) {
                    data[category].forEach((item) => {
                        // Push the description, or an empty string if it doesn't exist
                        result.push(item.desc || "");
                    });
                }
            }
            return result;
        },
        async downloadPDF() {
            this.isDownloading = true;
            this.isGeneratingPDF = true; 
            
            // Trigger Vuesax global loader
            this.$vs.loading({ type: 'radius' }); 

            // Allow Vue to render the table in the DOM before calculating height
            await this.$nextTick(); 
            await new Promise(resolve => setTimeout(resolve, 150)); 

            const reportWrapper = document.getElementById("report-pdf-wrapper");
            const tableContainer = document.getElementById("table-scroll-container");

            const originalOverflow = tableContainer.style.overflow;
            const originalWidth = tableContainer.style.width;
            const originalWrapperWidth = reportWrapper.style.width;

            const fullScrollWidth = tableContainer.scrollWidth;

            tableContainer.style.overflow = "visible";
            tableContainer.style.width = fullScrollWidth + "px";
            reportWrapper.style.width = (fullScrollWidth + 60) + "px";

            // Wait again for layout adjustments
            await this.$nextTick(); 

            // ... (keep all your existing image-to-base64 code here) ...

            // Because the table is inside `reportWrapper`, scrollHeight will now automatically 
            // be much larger, capturing both your original matrix and the new table underneath!
            const pdfWidth = reportWrapper.scrollWidth + 20; 
            const pdfHeight = reportWrapper.scrollHeight + 50;

            // Replace the old roleNameStr declaration inside downloadPDF with:
            const roleNameStr = (this.selectedRoleFilter.includes("All Roles") || this.selectedRoleFilter.length === 0) 
                ? "All_Roles" 
                : this.selectedRoleFilter.map(r => r.replace(/[^a-zA-Z0-9]/g, '_')).join('-').substring(0, 40); // substring prevents excessively long file names

            const opt = {
                margin:       [20, 20, 20, 20], 
                filename:     `Talent_Scorecard_${this.yAxis}_${roleNameStr}.pdf`,
                image:        { type: 'jpeg', quality: 1 },
                html2canvas:  { 
                    scale: 2,           
                    useCORS: true,      
                    allowTaint: true,   
                    windowWidth: pdfWidth, 
                    scrollX: 0,
                    scrollY: 0
                },
                jsPDF:        { 
                    unit: 'px', 
                    format: [pdfWidth, pdfHeight], 
                    orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait' 
                }
            };

            try {
                await html2pdf().set(opt).from(reportWrapper).save();
            } catch (err) {
                console.error("PDF Export Error: ", err);
            } finally {
                tableContainer.style.overflow = originalOverflow;
                tableContainer.style.width = originalWidth;
                reportWrapper.style.width = originalWrapperWidth;
                
                // (keep your existing image opacity reset code here)
                
                this.isGeneratingPDF = false; 
                this.isDownloading = false;
                
                // Close the global loader
                this.$vs.loading.close(); 
            }
        },

        handleClickOutside(event) {
        const dropdown = this.$refs.roleDropdown;
        if (dropdown && !dropdown.contains(event.target)) {
            this.isRoleDropdownOpen = false;
        }
    },

        openRoleCard(roleId) {
            if (!roleId) {
                this.$vs.notify({ title: 'Role ID Missing', text: 'Unable to load details for this role.', color: 'warning' });
                return;
            }
            this.selectedRoleId = roleId;
            this.isRoleModalOpen = true;
        },

        async openTalentCard(userId) {
            if (!userId) {
                this.$vs.notify({ title: 'User ID Missing', text: 'Unable to load details for this user.', color: 'warning' });
                return;
            }
            this.isTalentModalOpen = false;
            this.selectedUserData = null;
            this.$vs.loading({ type: 'radius' });

            try {
                const token = localStorage.getItem('accessToken');
                const baseUrl = 'https://api.getboarded.tech';
                
                const response = await fetch(`${baseUrl}/users/${userId}`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                });
                
                const json = await response.json();
                this.selectedUserId = userId;
                this.selectedUserData = json; 
                this.isTalentModalOpen = true; 

                this.$nextTick(() => {
                    setTimeout(() => {
                        const comp = this.$refs.talentCardRef;
                        if (comp) {
                            if ('popupActive' in comp) comp.popupActive = true;
                            if ('active' in comp) comp.active = true;
                            if ('showModal' in comp) comp.showModal = true;
                            if (comp.$el) {
                                const trigger = comp.$el.querySelector('svg') || comp.$el.querySelector('i') || comp.$el.querySelector('.vs-icon') || comp.$el;
                                if (trigger && typeof trigger.click === 'function') trigger.click();
                            }
                        }
                    }, 50);
                });
            } catch (err) {
                console.error("Error fetching user data:", err);
                this.$vs.notify({ title: 'Error', text: 'Failed to fetch user data.', color: 'danger' });
            } finally {
                this.$vs.loading.close();
            }
        },

        getUserById(userId) {
            if (!this.employeesData) return [];
            return this.employeesData.filter((user) => user.userId == userId);
        },

        async fetchOrgConfig() {
            let finalConfig = {};
            try {
                const orgId = this.orgData._id;
                const token = localStorage.getItem('accessToken'); 
                const baseUrl = 'https://api.getboarded.tech';
                
                const response = await fetch(`${baseUrl}/kvdr-org-config?orgId=${orgId}`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                });

                if (!response.ok) throw new Error("API request failed");
                
                const json = await response.json();
                
                if (json.data && json.data.length > 0) {
                    finalConfig = json.data[0];
                }
            } catch (err) {
                console.error("Error fetching config in Reports:", err);
            }

            if (!finalConfig.orgId || finalConfig.orgId === "000000000000000000000000" || finalConfig.orgId === "0") {
                try {
                    const slug = this.orgData.slug;
                    if (slug) {
                        const localDataStr = localStorage.getItem(`onboardingConfigs_${slug}`);
                        if (localDataStr) {
                            const localData = JSON.parse(localDataStr);
                            if (localData.appraisal) finalConfig.appraisalData = localData.appraisal;
                            if (localData.fitment) finalConfig.fitmentData = localData.fitment;
                            if (localData.succession) finalConfig.successionData = localData.succession;
                            if (localData.performance) finalConfig.performanceData = localData.performance;
                        }
                    }
                } catch (e) {
                    console.warn("Could not load local dynamic config", e);
                }
            }

            if (!finalConfig.appraisalData && finalConfig.appraisal) finalConfig.appraisalData = finalConfig.appraisal;
            if (!finalConfig.fitmentData && finalConfig.fitment) finalConfig.fitmentData = finalConfig.fitment;

            if (Object.keys(finalConfig).length === 0) {
                 finalConfig = kvdrOrgConfig(this.orgData._id);
            }

            this.kvdrConfig = finalConfig;
        },

        async createTalentScorecardData() {
            if (!this.orgData || !this.orgData._id) return;
            
            await this.fetchOrgConfig();

            const storeData = this.storeEvaluationMetrics || {};

            if (this.yAxis === "Fitment (Fit)") {
                this.key = "fitmentData";
            } else if (this.yAxis === "Performance (Drive)") {
                this.key = "performanceData";
            } else if (this.yAxis === "Succession (Secure)") {
                this.key = "successionData";
            } else {
                this.key = "appraisalData";
            }
            
            let configData = storeData[this.key] || this.kvdrConfig[this.key] || [];
            
            if (Object.keys(configData).length === 0 && this.kvdrConfig[this.key.replace('Data', '')]) {
                configData = this.kvdrConfig[this.key.replace('Data', '')];
            }

            // Using await here to process classification asynchronously
            this.assessmentData = {
                weightages: this.extractTalentCardWeightage(configData),
                scoreHeaders: this.extractTalentCardTitles(configData),
                scoreDescriptions: this.extractTalentCardDescriptions(configData), // <-- ADD THIS
                employees: await this.extractTalentCardUsers(configData), 
            };
        },
        
        async extractTalentCardUsers(activeConfigData) {
            if (!this.orgData || !this.kvdrData || !this.kvdrData.kvdrRoles) return [];

            const token = localStorage.getItem('accessToken');
            const baseUrl = process.env.VUE_APP_API_URL || 'https://api.getboarded.tech';

            // Extract unique role IDs to pre-fetch classification metrics in parallel
            const uniqueRoleIds = [...new Set(this.kvdrData.kvdrRoles.map(r => r.selfId || r._id))];
            const classificationMap = {};

            await Promise.all(uniqueRoleIds.map(async (roleId) => {
                try {
                    const res = await fetch(`${baseUrl}/api/v2/roles/${roleId}/kvdr-metrics`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const json = await res.json();
                        classificationMap[roleId] = json.data?.classification || 'NVR';
                    } else {
                        classificationMap[roleId] = 'NVR';
                    }
                } catch (e) {
                    classificationMap[roleId] = 'NVR';
                }
            }));

            const result = [];
            
            for (const index in this.kvdrData.kvdrRoles) {
                const kvdrRoles = this.kvdrData.kvdrRoles[index];
                const extractedRoleId = kvdrRoles.selfId || kvdrRoles._id;

                // Lookup fetched classification defaulting to NVR
                const roleClassification = classificationMap[extractedRoleId] || 'NVR';

                if (kvdrRoles.kvdrRoleProfiles && Array.isArray(kvdrRoles.kvdrRoleProfiles)) {
                    kvdrRoles.kvdrRoleProfiles.forEach((e) => {
                        const filteredUser = this.getUserById(e.userId);
                        
                        if (filteredUser && filteredUser.length > 0) {
                            const tmpUser = filteredUser[0];
                            if (!tmpUser) return;

                            const userDataToParse = e[this.key] || e[this.key.replace('Data', '_data')];
                            let userScore = this.extractUserScore(userDataToParse, activeConfigData);

                            const userData = tmpUser.userData || {};
                            const firstName = userData.firstName || 'User';
                            
                            let avatarUrl = userData.profilePictureUrl 
                                ? `${constants.s3Bucket}/editor/profile-pictures/${userData.profilePictureUrl}`
                                : `https://hostedboringavatars.vercel.app/api/beam?name=${firstName}`;

                            const rawTotal = userScore.reduce((sum, scoreObj) => sum + (scoreObj.value * scoreObj.weight), 0);
                            const calculatedTotal = Math.round(rawTotal);

                            result.push({
                                name: tmpUser.fullName || `${firstName} ${userData.lastName || ''}`,
                                userId: tmpUser.userId || e.userId, 
                                role: kvdrRoles.title,
                                roleId: extractedRoleId,
                                classification: roleClassification, // Added dynamic classification binding
                                avatar: avatarUrl,
                                scores: userScore,
                                totalScore: calculatedTotal,
                            });
                        }
                    });
                }
            }
            
            return result.sort((a, b) => a.role.localeCompare(b.role) || a.name.localeCompare(b.name));
        },

        extractTalentCardTitles(data) {
            if (!data) return [];
            const result = [];
            for (const category in data) {
                if (Array.isArray(data[category])) {
                    data[category].forEach((item) => {
                        result.push(item.title);
                    });
                }
            }
            return result;
        },

        extractUserScore(userProfileData, configData) {
            if (!configData) return [];
            const result = [];
            
            for (const category in configData) {
                if (Array.isArray(configData[category])) {
                    configData[category].forEach(attr => {
                        const compositeKey = `${category}|${attr.key}`;
                        const match = (userProfileData || []).find(d => 
                            d.key === compositeKey || d.key === attr.key
                        );

                        const weight = parseFloat(attr.value) || 0;

                        if (match) {
                            const val = parseFloat(match.score !== undefined ? match.score : match.value);
                            result.push({
                                value: isNaN(val) ? 0 : val,
                                weight: weight,
                                remarks: match.remarks || match.reasoning || "No Remarks available.",
                            });
                        } else {
                            result.push({ value: 0, weight: weight, remarks: "" });
                        }
                    });
                }
            }
            return result;
        },

        extractTalentCardWeightage(data) {
            if (!data) return [];
            const result = [];
            for (const category in data) {
                if (Array.isArray(data[category])) {
                    data[category].forEach((item) => {
                        result.push(item.value);
                    });
                }
            }
            return result;
        },

        getScoreColor(score) {
            if (score === null || score === undefined) return "#cbd5e1";

            const parsedScore = parseFloat(score);

            if (this.kvdrConfig && Array.isArray(this.kvdrConfig.calculationTypes)) {
                const match = this.kvdrConfig.calculationTypes.find(ct => parseFloat(ct.value) === parsedScore);
                if (match && match.color) {
                    return match.color;
                }
            }

            if (parsedScore >= 1.5) return "#22c55e"; 
            if (parsedScore >= 1) return "#f5bb00";  
            if (parsedScore < 0) return "#ef4444";   
            
            return "#cbd5e1"; 
        },

        getScoreTextColorClass(score) {
            if (score >= 80) return "text-green-600";
            if (score >= 60) return "text-primary";
            return "text-red-600";
        },

        handleSort(type, index = null) {
            if (this.sortKey === type && this.sortIndex === index) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortKey = type;
                this.sortIndex = index;
                this.sortOrder = 'asc';
            }

            const modifier = this.sortOrder === 'asc' ? 1 : -1;

            this.assessmentData.employees.sort((a, b) => {
                if (type === 'name') {
                    return a.name.localeCompare(b.name) * modifier;
                }else if (type === 'role') {
                    const roleCompare = a.role.localeCompare(b.role);
                    if (roleCompare !== 0) return roleCompare * modifier;
                    return a.name.localeCompare(b.name) * modifier;
                }  else if (type === 'total') {
                    return (a.totalScore - b.totalScore) * modifier;
                } else if (type === 'criteria') {
                    const valA = a.scores[index]?.value || 0;
                    const valB = b.scores[index]?.value || 0;
                    return (valA - valB) * modifier;
                }
                return 0;
            });
        },
    },
    mounted() {
        if (this.orgData && this.kvdrData) {
            this.createTalentScorecardData();
        }
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeDestroy() {
    // Clean up event listener when component unmounts
    document.removeEventListener('click', this.handleClickOutside);
},
};
</script>

<style scoped>
table {
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
    width: 100%;
}

th,
td {
    border: 1px solid #e5e7eb;
}

th {
    background-color: #f9fafb;
    font-weight: 600;
    color: #4b5563;
}

.name {
    width: 250px !important;
    min-width: 250px !important;
}

td,
th {
    width: 110px;
}

/* Added Badge Styles */
.role-badge {
    padding: 2px 6px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 700;
    display: inline-block;
    letter-spacing: 0.5px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}
.role-badge.nvr { background: #f1f5f9; color: #64748b; border: 1px solid #cbd5e1; }
.role-badge.svr {background: #e0f2fe; color: #0284c7; border: 1px solid #bae6fd; }
.role-badge.kvdr { background: #E6F7F0; color: #067647; border: 1px solid #A3E6C8; }
</style>